import { Fragment, useState } from "react";
import {
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";
import { styled } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { useAlert, useAxios } from "../../hooks";
import { Label, TextField } from "../../components";
import { useDispatch } from "react-redux";

const HeaderBox = styled(Box)`
  text-align: center;
  margin-bottom: 32px;
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    border-radius: 8px;
    background-color: #f8f9fa;

    & fieldset {
      border-color: #e0e0e0;
    }

    &:hover fieldset {
      border-color: #667eea;
    }

    &.Mui-focused fieldset {
      border-color: #667eea;
      border-width: 2px;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 14px 16px;
  }
`;

const StyledLoadingButton = styled(LoadingButton)`
  border-radius: 8px;
  padding: 12px;
  font-weight: 600;
  text-transform: none;
  font-size: 16px;
  background: linear-gradient(
    135deg,
    rgba(66, 63, 255, 1) 0%,
    rgba(23, 20, 240, 1) 100%
  );
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(66, 63, 255, 1) 0%,
      rgba(23, 20, 240, 1) 100%
    );
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
  }

  &.Mui-disabled {
    background: #e0e0e0;
    color: #9e9e9e;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 8px;
  padding: 12px;
  font-weight: 600;
  text-transform: none;
  font-size: 16px;
  border: 2px solid #667eea;
  color: #667eea;

  &:hover {
    border: 2px solid #667eea;
    background-color: rgba(102, 126, 234, 0.04);
  }
`;

const RememberMeBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

export default function FormDisplay({
  otpSent,
  setOtpSent,
  isOtpLogin,
  formikProps,
  setIsOtpLogin,
}) {
  const axios = useAxios();
  const dispatch = useDispatch();
  const { alert } = useAlert();
  const [showPassword, setShowPassword] = useState(false);

  const toggleLoginMethod = () => {
    setIsOtpLogin((prev) => !prev);
    formikProps.resetForm();
    setOtpSent(false);
  };

  const handleSendOtp = () => {
    if (!formikProps.values.mobile) return;

    axios({
      url: `event/booking/send-otp?&visitor=false&mobile_no=${formikProps.values.mobile}`,
      method: "POST",
    })
      .then((response) => {
        if (response.status) {
          setOtpSent(true);
          dispatch(
            alert({
              type: "success",
              message:
                response.message.displayMessage || "OTP sent successfully!",
            })
          );
        }
      })
      .catch((error) => {
        console.error("OTP send error", error);
      });
  };

  return (
    <Fragment>
      <Grid item xs={12}>
        <img
          src="https://dolphinchat-chat.s3.ap-south-1.amazonaws.com/mpsedc/digipass/events1765166449543_1765166452253_goenka.jpg"
          alt="Sign In"
          style={{
            width: "100%",
            maxHeight: "200px",
            objectFit: "contain",
            borderRadius: "5px",
          }}
        />
      </Grid>
      <HeaderBox>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            color: "#1a1a1a",
            marginBottom: "8px",
          }}
        >
          Welcome
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#757575",
            fontSize: "15px",
          }}
        >
          Enter your credentials to access your account
        </Typography>
      </HeaderBox>

      <Box>
        {isOtpLogin ? (
          <>
            <Box mb={3}>
              <Label
                required
                title="Mobile Number"
                formikProps={formikProps}
                sx={{
                  fontWeight: 600,
                  color: "#424242",
                  fontSize: "14px",
                  marginBottom: "8px",
                }}
              />
              <StyledTextField
                required
                name="mobile"
                placeholder="Enter mobile number"
                formikProps={formikProps}
                fullWidth
              />
            </Box>

            {otpSent && (
              <Box mb={3}>
                <Label
                  required
                  title="OTP"
                  formikProps={formikProps}
                  sx={{
                    fontWeight: 600,
                    color: "#424242",
                    fontSize: "14px",
                    marginBottom: "8px",
                  }}
                />
                <StyledTextField
                  required
                  name="otp"
                  placeholder="Enter OTP"
                  formikProps={formikProps}
                  fullWidth
                />
              </Box>
            )}
          </>
        ) : (
          <>
            <Box mb={3}>
              <Label
                required
                title="Email or Username"
                formikProps={formikProps}
                sx={{
                  fontWeight: 600,
                  color: "#424242",
                  fontSize: "14px",
                  marginBottom: "8px",
                }}
              />
              <StyledTextField
                required
                name="email"
                placeholder="user@example.com"
                formikProps={formikProps}
                fullWidth
              />
            </Box>

            <Box mb={2}>
              <Label
                required
                title="Password"
                formikProps={formikProps}
                sx={{
                  fontWeight: 600,
                  color: "#424242",
                  fontSize: "14px",
                  marginBottom: "8px",
                }}
              />
              <StyledTextField
                required
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                formikProps={formikProps}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* <RememberMeBox>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{
                      color: "#667eea",
                      "&.Mui-checked": {
                        color: "#667eea",
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "14px", color: "#424242" }}>
                    Remember me
                  </Typography>
                }
              />
              <Link
                href="#"
                underline="none"
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#667eea",
                  "&:hover": {
                    color: "#5568d3",
                  },
                }}
              >
                Forgot?
              </Link>
            </RememberMeBox> */}
          </>
        )}

        <Box mt={4} mb={2}>
          {isOtpLogin ? (
            !otpSent ? (
              <StyledLoadingButton
                type="button"
                variant="contained"
                fullWidth
                onClick={handleSendOtp}
                loading={formikProps.isSubmitting}
              >
                Send OTP
              </StyledLoadingButton>
            ) : (
              <StyledLoadingButton
                type="submit"
                variant="contained"
                fullWidth
                onClick={formikProps.handleSubmit}
                loading={formikProps.isSubmitting}
                disabled={!formikProps.dirty || !formikProps.isValid}
              >
                Verify OTP
              </StyledLoadingButton>
            )
          ) : (
            <StyledLoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={formikProps.isSubmitting}
              disabled={!formikProps.dirty || !formikProps.isValid}
            >
              Sign In
            </StyledLoadingButton>
          )}
        </Box>

        <Box>
          <StyledButton
            variant="outlined"
            fullWidth
            onClick={toggleLoginMethod}
          >
            {isOtpLogin ? "Sign in with Email & Password" : "Sign in with OTP"}
          </StyledButton>
        </Box>
      </Box>
    </Fragment>
  );
}
